/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable @typescript-eslint/no-explicit-any */
import { memo } from 'react';
import { Checkbox, TableHead, TableRow, TableSortLabel } from '@mui/material';
import { ICmnSubSearchCondition, ICustomDefaultProps, ICustomTableHeadWrapperProps } from 'interface/cmn/commonInfo';
import uniqueId from 'lodash/uniqueId';
import {
  AccountsContent,
  IAccountProjectListHeadCellProps,
  ICompAccountsHeadCellProps,
  IContent,
  IDiagnosticHeadCellProps,
  IDiagnosticHistoryHeadCellProps,
  IProjectListHeadCellProps,
  IProjectUsersListHeadCellProps,
  ProjectUserContent,
  SurveyListLatestContent,
} from 'interface/project/projectInfo';
import {
  accountManageListHeadCells,
  accountProjectListHeadCells,
  compAccountsListHeadCells,
  diagnosticHeadCells,
  diagnosticHistoryHeadCells,
  projectListHeadCells,
  projectUsersListHeadCells,
} from 'libs/tableUtils';
import { useProject } from 'hooks/useProject';
import CustomTableCell from './CustomTableCell';

function CustomTableHeadWrapper<T, K>({
  order,
  orderBy,
  sortArray,
  headCells,
  handleChangeSort,
  isCheck = false,
  numSelected = 0,
  rowCount = 0,
  onSelectAllClick = () => {},
  sx = {},
  customStyles = {},
  checkStyles = {},
}: ICustomTableHeadWrapperProps<T, K>): JSX.Element {
  return (
    <TableHead>
      <TableRow>
        {isCheck && (
          <CustomTableCell align="center" customStyles={customStyles}>
            <Checkbox
              indeterminate={numSelected > 0 && numSelected < rowCount}
              checked={rowCount > 0 && numSelected === rowCount}
              onChange={onSelectAllClick}
              inputProps={{
                'aria-label': 'select table',
              }}
              sx={{
                color: 'primary',
                ...checkStyles,
              }}
            />
          </CustomTableCell>
        )}
        {headCells.map(headCell => (
          <CustomTableCell
            key={`${headCell.id as string}-${uniqueId()}`}
            align={headCell.align}
            padding={headCell.disablePadding ? 'none' : 'normal'}
            sortDirection={(orderBy as string) === headCell.id ? order : false}
            width={headCell?.width}
          >
            {sortArray.includes(headCell.id as string) ? (
              <TableSortLabel
                active={(orderBy as string) === headCell.id}
                direction={(orderBy as string) === headCell.id ? order : 'asc'}
                onClick={handleChangeSort(headCell.id)}
              >
                <span style={{ verticalAlign: 'middle' }}>{headCell.label}</span>
              </TableSortLabel>
            ) : (
              <span style={{ verticalAlign: 'middle' }}>{headCell.label}</span>
            )}
          </CustomTableCell>
        ))}
      </TableRow>
    </TableHead>
  );
}
export default memo(CustomTableHeadWrapper);

export function ProjectTableHead({ order, orderBy }: ICmnSubSearchCondition<IContent>): JSX.Element {
  const { changeProjectListSubSearchCondition } = useProject();
  const sortArray: string[] = [];
  const handleChangeSort = (newOrderBy: any) => () => {
    const isAsc = orderBy === newOrderBy && order === 'asc';
    changeProjectListSubSearchCondition(isAsc ? 'desc' : 'asc', newOrderBy);
  };
  return (
    <CustomTableHeadWrapper<IContent, IProjectListHeadCellProps>
      order={order}
      orderBy={orderBy}
      headCells={projectListHeadCells}
      sortArray={sortArray}
      handleChangeSort={handleChangeSort}
    />
  );
}
export function CompAccountsTableHead({ order, orderBy }: ICmnSubSearchCondition<AccountsContent>): JSX.Element {
  const sortArray: string[] = [];
  const handleChangeSort = (newOrderBy: any) => () => {};
  return (
    <CustomTableHeadWrapper<AccountsContent, ICompAccountsHeadCellProps>
      order={order}
      orderBy={orderBy}
      headCells={compAccountsListHeadCells}
      sortArray={sortArray}
      handleChangeSort={handleChangeSort}
    />
  );
}

export interface ICommonTableHeadProps<T> extends ICmnSubSearchCondition<T>, ICustomDefaultProps {}
export function UsersListTableHead({
  order,
  orderBy,
  numSelected,
  onSelectAllClick,
  rowCount,
  isCheck,
  ...others
}: ICommonTableHeadProps<ProjectUserContent>): JSX.Element {
  const sortArray: string[] = [];
  const handleChangeSort = (newOrderBy: any) => () => {};
  return (
    <CustomTableHeadWrapper<ProjectUserContent, IProjectUsersListHeadCellProps>
      order={order}
      orderBy={orderBy}
      headCells={projectUsersListHeadCells}
      sortArray={sortArray}
      handleChangeSort={handleChangeSort}
      numSelected={numSelected}
      onSelectAllClick={onSelectAllClick}
      rowCount={rowCount}
      isCheck={isCheck}
      {...others}
    />
  );
}
export function AccountManageTableHead({ order, orderBy }: ICmnSubSearchCondition<AccountsContent>): JSX.Element {
  const sortArray: string[] = [];
  const handleChangeSort = (newOrderBy: any) => () => {};
  return (
    <CustomTableHeadWrapper<AccountsContent, ICompAccountsHeadCellProps>
      order={order}
      orderBy={orderBy}
      headCells={accountManageListHeadCells}
      sortArray={sortArray}
      handleChangeSort={handleChangeSort}
    />
  );
}

export function AccountProjectTableHead({ order, orderBy }: ICmnSubSearchCondition<IContent>): JSX.Element {
  const sortArray: string[] = [];
  const handleChangeSort = (newOrderBy: any) => () => {};
  return (
    <CustomTableHeadWrapper<IContent, IAccountProjectListHeadCellProps>
      order={order}
      orderBy={orderBy}
      headCells={accountProjectListHeadCells}
      sortArray={sortArray}
      handleChangeSort={handleChangeSort}
    />
  );
}

export function DiagnosticTableHead({ order, orderBy }: ICmnSubSearchCondition<SurveyListLatestContent>): JSX.Element {
  const sortArray: string[] = [];
  const handleChangeSort = (newOrderBy: any) => () => {};
  return (
    <CustomTableHeadWrapper<SurveyListLatestContent, IDiagnosticHeadCellProps>
      order={order}
      orderBy={orderBy}
      headCells={diagnosticHeadCells}
      sortArray={sortArray}
      handleChangeSort={handleChangeSort}
    />
  );
}

export function DiagnosticHistoryTableHead({
  order,
  orderBy,
}: ICmnSubSearchCondition<SurveyListLatestContent>): JSX.Element {
  const sortArray: string[] = [];
  const handleChangeSort = (newOrderBy: any) => () => {};
  return (
    <CustomTableHeadWrapper<SurveyListLatestContent, IDiagnosticHistoryHeadCellProps>
      order={order}
      orderBy={orderBy}
      headCells={diagnosticHistoryHeadCells}
      sortArray={sortArray}
      handleChangeSort={handleChangeSort}
    />
  );
}
